<template>
  <div>
    <div class="d-flex justify-space-between align-center px-1">
      <AppHeading>Orders</AppHeading>
      <AddOnsSwitch
        v-if="hasAddOns"
        v-model="showAddOns"
        section-name="Orders"
      />
    </div>
    <div class="pa-1">
      <VExpansionPanels v-if="orders.length" v-model="openPanels" multiple>
        <template v-for="order in orders">
          <Order
            v-if="salesforceOrder(order)"
            v-show="!isFiltering || isOrderVisible(order.orderNumber)"
            :key="order.orderNumber"
            :class="{
              'search-highlight': highlightOrderNumber === order.orderNumber
            }"
            :order="order"
            :salesforce-order="salesforceOrder(order)"
            :proofs="proofs"
            :all-orders="orders"
            :show-add-ons="showAddOns"
            :filter-proof-ids="filterProofIds"
          />
        </template>
      </VExpansionPanels>
      <div v-else class="text-body-2 text--secondary">None</div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue'

import AddOnsSwitch from '@/components/AddOnsSwitch.vue'
import AppHeading from '@/components/AppHeading.vue'
import { recentOpenPanels } from '@/components/mixins/panelUtils'
import OrderComponent from '@/components/Order.vue'
import { Order as SalesforceOrder, Proof } from '@/generated'
import { Order } from '@/store/modules/types/order'

export default defineComponent({
  components: {
    AddOnsSwitch,
    AppHeading,
    Order: OrderComponent
  },
  props: {
    orders: {
      type: Array as PropType<Order[]>,
      required: true
    },
    salesforceOrders: {
      type: Array as PropType<SalesforceOrder[]>,
      required: true
    },
    proofs: {
      type: Array as PropType<Proof[]>,
      required: true
    },
    highlightOrderNumber: {
      type: String as PropType<string | null>,
      default: null
    },
    filterOrderNumbers: {
      type: Array as PropType<string[] | null>,
      default: null
    },
    filterProofIds: {
      type: Array as PropType<string[] | null>,
      default: null
    }
  },
  setup(props) {
    const openPanels = recentOpenPanels(
      props.orders.map((order: Order) => new Date(order.created)),
      'Order'
    )

    // When a highlight order is set (e.g. from proof cross-reference),
    // force that order open — may arrive after initial render
    watch(
      () => props.highlightOrderNumber,
      (orderNum) => {
        if (!orderNum) return
        const idx = props.orders.findIndex((o) => o.orderNumber === orderNum)
        if (idx >= 0 && !openPanels.value.includes(idx)) {
          openPanels.value = [idx, ...openPanels.value]
        }
      },
      { immediate: true }
    )

    const isFiltering = computed(() => props.filterOrderNumbers !== null)
    const filterSet = computed(() => new Set(props.filterOrderNumbers || []))

    // When filter activates, force filtered orders open
    watch(
      () => props.filterOrderNumbers,
      (nums) => {
        if (!nums || !nums.length) return
        const indices = nums
          .map((n) => props.orders.findIndex((o) => o.orderNumber === n))
          .filter((i) => i >= 0)
        if (indices.length) {
          const merged = new Set([...indices, ...openPanels.value])
          openPanels.value = [...merged]
        }
      }
    )

    return {
      isFiltering,
      isOrderVisible(orderNumber: string) {
        return filterSet.value.has(orderNumber)
      },
      isOrderFiltered(orderNumber: string) {
        return isFiltering.value && filterSet.value.has(orderNumber)
      },
      salesforceOrder(order: Order) {
        return props.salesforceOrders.find(
          (salesforceOrder) => salesforceOrder.orderNumber == order.orderNumber
        )
      },
      hasAddOns: computed(() =>
        props.orders.some((order) =>
          order.orderItems.some(
            (item) => item.proof && item.proof.projectId !== item.proofId
          )
        )
      ),
      openPanels,
      showAddOns: ref(false)
    }
  }
})
</script>
